// src/pages/Dashboard/Admin/Votes/utils/votesDashboardUtils.js

export const DEFAULT_VOTE_FILTERS = {
  query: "",
  type: "",
  status: "",
};

export const VOTE_TYPES = [
  { value: "vote", label: "Vote" },
  { value: "sondage", label: "Sondage" },
];

export const VOTE_STATUS_OPTIONS = [
  { value: "active", label: "Actif" },
  { value: "scheduled", label: "Programmé" },
  { value: "closed", label: "Clôturé" },
  { value: "inactive", label: "Inactif" },
];

export const QUESTION_TYPES = [
  { value: "single", label: "Réponse unique" },
  { value: "multiple", label: "Réponses multiples" },
  { value: "open", label: "Réponse libre" },
];

const pad = (value) => String(value).padStart(2, "0");

export function safeArray(value) {
  return Array.isArray(value) ? value : [];
}

export function normalizeText(value = "") {
  return String(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

export function stripHtml(html = "") {
  return String(html)
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&quot;/g, '"')
    .replace(/&#039;/g, "'")
    .replace(/\s+/g, " ")
    .trim();
}

export function isEmptyHtml(html) {
  return stripHtml(html).length === 0;
}

export function truncateText(value = "", max = 180) {
  const text = stripHtml(value || "");

  if (text.length <= max) return text;

  return `${text.slice(0, max).trim()}…`;
}

export function formatDateTimeFR(value) {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "—";

  return new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  })
    .format(date)
    .replace(",", " -");
}

export function formatDateShort(value) {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "—";

  return new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  }).format(date);
}

export function toLocalDatetimeInput(dateLike = new Date()) {
  if (!dateLike) return "";

  const date = dateLike instanceof Date ? dateLike : new Date(dateLike);

  if (Number.isNaN(date.getTime())) return "";

  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());

  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

export function getDefaultVoteForm() {
  const now = new Date();
  const inSevenDays = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);

  return {
    title: "",
    description: "",
    start_time: toLocalDatetimeInput(now),
    end_time: toLocalDatetimeInput(inSevenDays),
    type: "vote",
    is_active: true,
  };
}

export function mapVoteToForm(vote) {
  if (!vote) return getDefaultVoteForm();

  return {
    title: vote.title || "",
    description: vote.description || "",
    start_time: toLocalDatetimeInput(vote.start_time),
    end_time: toLocalDatetimeInput(vote.end_time),
    type: vote.type || "vote",
    is_active: vote.is_active ?? true,
  };
}

export function validateVoteForm(formData) {
  const errors = {};

  if (!String(formData.title || "").trim()) {
    errors.title = "Le titre est requis.";
  }

  if (!formData.start_time) {
    errors.start_time = "La date de début est requise.";
  }

  if (!formData.end_time) {
    errors.end_time = "La date de fin est requise.";
  }

  if (formData.start_time && formData.end_time) {
    const start = new Date(formData.start_time);
    const end = new Date(formData.end_time);

    if (end <= start) {
      errors.range = "La date de fin doit être postérieure au début.";
    }
  }

  return errors;
}

export function buildVotePayload(formData) {
  return {
    title: String(formData.title || "").trim(),
    description: String(formData.description || "").trim(),
    start_time: formData.start_time,
    end_time: formData.end_time,
    type: formData.type || "vote",
    is_active: !!formData.is_active,
  };
}

export function getVoteStatusMeta(vote) {
  const now = new Date();
  const start = vote?.start_time ? new Date(vote.start_time) : null;
  const end = vote?.end_time ? new Date(vote.end_time) : null;

  if (start && now < start) {
    return {
      key: "scheduled",
      label: "Programmé",
      badge:
        "bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/20",
      row:
        "bg-amber-50/50 hover:bg-amber-100/60 dark:bg-amber-500/5 dark:hover:bg-amber-500/10",
    };
  }

  if (end && now > end) {
    return {
      key: "closed",
      label: "Clôturé",
      badge:
        "bg-slate-100 text-slate-700 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700",
      row:
        "bg-slate-50/80 hover:bg-slate-100/80 dark:bg-slate-900 dark:hover:bg-slate-800/70",
    };
  }

  if (vote?.is_active) {
    return {
      key: "active",
      label: "Actif",
      badge:
        "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20",
      row:
        "bg-emerald-50/50 hover:bg-emerald-100/60 dark:bg-emerald-500/5 dark:hover:bg-emerald-500/10",
    };
  }

  return {
    key: "inactive",
    label: "Inactif",
    badge:
      "bg-rose-50 text-rose-700 ring-rose-200 dark:bg-rose-500/10 dark:text-rose-300 dark:ring-rose-500/20",
    row:
      "bg-rose-50/50 hover:bg-rose-100/60 dark:bg-rose-500/5 dark:hover:bg-rose-500/10",
  };
}

export function getVoteTypeMeta(type) {
  if (String(type || "").toLowerCase() === "sondage") {
    return {
      label: "Sondage",
      badge:
        "bg-violet-50 text-violet-700 ring-violet-200 dark:bg-violet-500/10 dark:text-violet-300 dark:ring-violet-500/20",
    };
  }

  return {
    label: "Vote",
    badge:
      "bg-sky-50 text-sky-700 ring-sky-200 dark:bg-sky-500/10 dark:text-sky-300 dark:ring-sky-500/20",
  };
}

export function getQuestionTypeLabel(type) {
  switch (type) {
    case "multiple":
      return "Réponses multiples";

    case "open":
      return "Réponse libre";

    case "single":
    default:
      return "Réponse unique";
  }
}

export function sortVotes(votes) {
  return safeArray(votes)
    .slice()
    .sort((a, b) => {
      const dateA = new Date(a?.start_time || a?.created_at || 0).getTime();
      const dateB = new Date(b?.start_time || b?.created_at || 0).getTime();

      if (dateA !== dateB) return dateB - dateA;

      return String(a?.title || "").localeCompare(String(b?.title || ""), "fr");
    });
}

export function filterVotes(votes, filters) {
  const query = normalizeText(filters?.query || "");
  const type = filters?.type || "";
  const status = filters?.status || "";

  return sortVotes(votes).filter((vote) => {
    const searchable = normalizeText(
      `${vote?.title || ""} ${vote?.description || ""} ${vote?.type || ""}`
    );

    const statusMeta = getVoteStatusMeta(vote);

    const matchQuery = !query || searchable.includes(query);
    const matchType = !type || vote?.type === type;
    const matchStatus = !status || statusMeta.key === status;

    return matchQuery && matchType && matchStatus;
  });
}

export function calculateVotesStats(votes) {
  const rows = safeArray(votes);

  let active = 0;
  let scheduled = 0;
  let closed = 0;
  let inactive = 0;
  let voteCount = 0;
  let surveyCount = 0;
  let questions = 0;
  let voters = 0;

  for (const vote of rows) {
    const status = getVoteStatusMeta(vote).key;

    if (status === "active") active += 1;
    if (status === "scheduled") scheduled += 1;
    if (status === "closed") closed += 1;
    if (status === "inactive") inactive += 1;

    if (String(vote?.type || "").toLowerCase() === "sondage") surveyCount += 1;
    else voteCount += 1;

    questions += Number(vote?.questionCount || 0);
    voters += Number(vote?.voterCount || 0);
  }

  return {
    total: rows.length,
    active,
    scheduled,
    closed,
    inactive,
    voteCount,
    surveyCount,
    questions,
    voters,
  };
}

export function sumChoiceCounts(choices = []) {
  return safeArray(choices).reduce(
    (sum, choice) => sum + (Number(choice?.count) || 0),
    0
  );
}

export function getPercent(value, total) {
  const number = Number(value || 0);

  if (!total) return 0;

  return Math.round((number * 100) / total);
}